import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import {
	EventCategory,
	GetOfflinePaymentOrRefundUnrecordedEventPropsEventFragment,
} from '#types';
import { toSimpleLink } from '../../../lib/helpers';
import rpcShared from '@rockpapercoin/rpc-shared';
import { FlattenedUser } from '../../../types/user';

export const getOfflinePaymentOrRefundUnrecordedEventPropsEventFragment = gql`
	fragment getOfflinePaymentOrRefundUnrecordedEventPropsEvent on Event {
		id
		category
		actingUser {
			id
			orgUser {
				id
				firstName
				lastName
			}
		}
		invoice {
			id
			title
			organization {
				id
				name
			}
		}
	}
`;

export const getOfflinePaymentOrRefundUnrecordedEventProps = (
	eventProps: {
		event: GetOfflinePaymentOrRefundUnrecordedEventPropsEventFragment;
		message: React.JSX.Element;
		links: {
			type: string;
			text: string;
			link: any;
			onClick?: ( () => any ) | null;
		}[];
		icon: string;
		className: string;
	},
	user: FlattenedUser
) => {
	if ( !eventProps.event.invoice ) {
		throw new Error( `Event ID ${ eventProps.event.id } missing required data` );
	}

	const { invoice } = eventProps.event;

	const paymentWasUnrecorded =
		eventProps.event.category ===
		EventCategory.OfflineInstallmentPaymentUnRecordedByVendor
			? true
			: false;
	const viewingUserIsCustomer = user?.isLoggedIn
		? rpcShared.invoices.isCustomer( invoice, user.user.id )
		: false;
	const viewingUserWasActingUser =
		user?.isLoggedIn && eventProps.event.actingUser?.id === user?.user?.id
			? true
			: false;
	const orgName = invoice.organization?.name;
	const actingUserName =
		eventProps.event.actingUser?.orgUser?.firstName +
		' ' +
		eventProps.event.actingUser?.orgUser?.lastName;

	const invoiceLinkProps = {
		text: invoice.title,
		link: {
			as: `/invoice/${ invoice.id }`,
			href: '/invoice/[id]',
		},
	};
	const invoiceLink = toSimpleLink( invoiceLinkProps );

	const defaultEventProps = {
		icon: 'dollar',
		links: [
			{
				type: 'secondary',
				text: 'View',
				link: invoiceLinkProps.link,
			},
		],
	};

	if ( viewingUserIsCustomer ) {
		return {
			...defaultEventProps,
			message: (
				<Typography>
					{ orgName } has unrecorded an offline{ ' ' }
					{ paymentWasUnrecorded ? 'payment' : 'refund' } on { invoiceLink }.
				</Typography>
			),
		};
	} else if ( viewingUserWasActingUser ) {
		return {
			...defaultEventProps,
			message: (
				<Typography>
					You have unrecorded an offline{ ' ' }
					{ paymentWasUnrecorded ? 'payment' : 'refund' } on { invoiceLink }.
				</Typography>
			),
		};
	} else {
		return {
			...defaultEventProps,
			message: (
				<Typography>
					{ actingUserName } has unrecorded an offline{ ' ' }
					{ paymentWasUnrecorded ? 'payment' : 'refund' } on { invoiceLink }.
				</Typography>
			),
		};
	}
};
